// Personal rail layout: each user can rename, add, remove and reorder the rail's groups,
// move links between them, hide links they never use, and pick how the rail shows them (style:
// full list, collapsible groups, icon rail, or pinned links + All pages: railStyles.js). Saved per user at
// userPrefs/{uid}/railLayout and cached on the device so the rail paints in the user's
// order before the database answers.
//
// The rail's buttons are moved, never rebuilt, so every click handler app.js wired to
// them keeps working. Hidden links stay in the DOM (in a display:none holder) because
// other code opens pages by .click()-ing them.
//
// Hubs (railHubs.js) are ordinary items. A hub's pages are tabs; layout.popouts lists the pages
// the user also gave their own rail link — those are ordinary items too (movable, pinnable,
// hideable) and stay tabs in their hub.

import { ref, get, set } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, database } from './initFirebase';
import { escapeHTML } from './utilities';
import { applyRailStyle, RAIL_STYLES } from './railStyles';
import { HUBS, MEMBER_KEYS, isHub, hubOf, memberTab, hubTabs, setPopouts, syncHubs } from './railHubs';

const CACHE_KEY = 'railLayout:v2';
// What the Pinned style shows until someone pins their own (links that aren't there are skipped).
const DEFAULT_PINS = ['hub-dashboard', 'open-work-button', 'haul-launch-container', 'scheduler-open-button', 'customers-open-button', 'hub-inbox', 'map-open-button'];
const STYLE_IDS = RAIL_STYLES.map(x => x.id);
// Elements that aren't rail-item buttons themselves but move as one unit.
const WRAPPERS = { 'haul-launch-container': 'haul-launch-open-button' };
// Items that were one unit in older saved layouts: Events and Email used to be a pair.
const SPLIT = { 'rail-glance-pair': ['calendar-open-btn', 'email-open-button'] };
const unsplit = keys => keys.flatMap(k => SPLIT[k] || [k]);

/* ============ SAVED LAYOUTS FROM BEFORE HUBS ============ */

// The Moorings and Fuel flyout buttons became their hubs.
const OLD_HUB = { 'mooring-open-button': 'hub-moorings', 'fuel-open-button': 'hub-fuel' };
// Now in the glance strip and the user menu, not the movable rail.
const GONE = new Set(['open-btn', 'calendar-open-btn', 'email-open-button', 'help-open-button', 'rail-customize-button', 'rail-glance-pair']);
const OLD_DEFAULT_PINS = ['dashboard-open-button', 'open-work-button', 'haul-launch-container', 'scheduler-open-button', 'customers-open-button', 'messages-rail-button', 'map-open-button'];
const hubFor = k => OLD_HUB[k] || hubOf(k);

/**
 * A layout saved before hubs (no popouts) in hub terms, so nobody loses their arrangement: a hub
 * takes the place of its first page in the groups; it is hidden only if all its pages were; a page
 * pinned in the Pinned style keeps its one-click link as a popout. Saved back only on the next Save.
 */
function migrate(layout) {
  if (!layout || Array.isArray(layout.popouts)) return layout;
  const out = { ...layout, popouts: [] };
  const hidden = unsplit(Array.isArray(layout.hidden) ? layout.hidden : []);
  const seen = {}; // hub → { shown, hidden } pages in the saved layout
  const note = (k, how) => { const h = hubFor(k); if (h) { seen[h] = seen[h] || { shown: 0, hidden: 0 }; seen[h][how]++; } };
  if (Array.isArray(layout.groups)) {
    layout.groups.forEach(g => unsplit(Array.isArray(g?.items) ? g.items : []).forEach(k => note(k, 'shown')));
    const placed = new Set();
    out.groups = layout.groups.map(g => ({
      name: g?.name,
      items: unsplit(Array.isArray(g?.items) ? g.items : []).flatMap(k => {
        if (GONE.has(k)) return [];
        const h = hubFor(k);
        if (!h) return [k];
        if (placed.has(h)) return [];
        placed.add(h);
        return [h];
      }),
    }));
  }
  hidden.forEach(k => note(k, 'hidden'));
  out.hidden = [...new Set(hidden.flatMap(k => {
    if (GONE.has(k)) return [];
    const h = hubFor(k);
    if (!h) return [k];
    return seen[h] && !seen[h].shown ? [h] : [];
  }))];
  if (Array.isArray(layout.pinned)) {
    let pinned = unsplit(layout.pinned).filter(k => !GONE.has(k));
    if (JSON.stringify(pinned) === JSON.stringify(OLD_DEFAULT_PINS)) pinned = [...DEFAULT_PINS];
    else if (layout.style === 'pinned') {
      pinned = pinned.map(k => {
        if (OLD_HUB[k]) return OLD_HUB[k];
        if (hubOf(k)) out.popouts.push(k);
        return k;
      });
    } else pinned = pinned.map(k => hubFor(k) || k);
    out.pinned = [...new Set(pinned)];
  }
  return out;
}

/** Put a popped-out page right after its hub (or, with the hub hidden, in the hub's home group). */
function placeAfterHub(layout, key) {
  const hub = hubOf(key);
  const g = layout.groups.find(x => x.items.includes(hub));
  if (g) { g.items.splice(g.items.indexOf(hub) + 1, 0, key); return; }
  const home = defaults.groups.find(dg => dg.items.includes(hub));
  const target = (home && layout.groups.find(x => x.name.toLowerCase() === home.name.toLowerCase())) || layout.groups[layout.groups.length - 1];
  target.items.push(key);
}

/** Take a popped-out page off the rail everywhere (it stays a tab in its hub). */
function dropPopout(layout, key) {
  layout.popouts = (layout.popouts || []).filter(k => k !== key);
  layout.groups.forEach(g => { g.items = g.items.filter(k => k !== key); });
  layout.hidden = layout.hidden.filter(k => k !== key);
  layout.pinned = (layout.pinned || []).filter(k => k !== key);
}

// Links for features the marina switched off (Settings › Features, marinaFeatures.js) carry
// .feature-off: they stay where the user put them but don't show, aren't offered in the
// editor, and don't keep an otherwise-empty group header alive.
const switchedOff = key => !!document.getElementById(key)?.classList.contains('feature-off');

let defaults = null; // { groups: [{ name, items: [key] }] } — read from the markup
let current = null;  // the layout on screen
let uid = null;

/* ============ ITEMS ============ */

const bar = () => document.querySelector('#buttonBar .page-buttons');

// Each direct child of .page-buttons that isn't a group header is one movable item,
// keyed by its id.
function itemKeyOf(el) {
  if (el.id) return el.id;
  if (el.classList.contains('haul-launch-container')) { el.id = 'haul-launch-container'; return el.id; }
  return null;
}

function itemLabel(key) {
  const el = document.getElementById(key);
  if (!el) return key;
  const labels = [...el.querySelectorAll('.rail-label')].map(l => l.textContent.trim()).filter(Boolean);
  return labels.join(' & ') || el.getAttribute('aria-label') || key;
}

function itemIcon(key) {
  const el = document.getElementById(WRAPPERS[key] || key);
  return el?.querySelector('img.icon-brand')?.getAttribute('src') || '';
}

function readDefaults() {
  const groups = [];
  [...bar().children].forEach(el => {
    if (el.classList.contains('rail-group-header')) groups.push({ name: el.textContent.trim(), items: [] });
    else if (!['rail-hidden-items', 'rail-hub-members', 'rail-offstage'].includes(el.id)) {
      const key = itemKeyOf(el);
      if (!key) return;
      if (!groups.length) groups.push({ name: '', items: [] });
      groups[groups.length - 1].items.push(key);
    }
  });
  return { groups, hidden: [], style: 'list', pinned: DEFAULT_PINS.filter(k => groups.some(g => g.items.includes(k))), popouts: [] };
}

/* ============ LAYOUT ============ */

// Fits a saved layout to the rail as it is today: drops links that no longer exist and
// puts any link the layout doesn't mention (new features) back in its default group.
function normalize(layout) {
  if (!layout) return clone(defaults);
  layout = migrate(layout);
  // A saved style or pins without groups (default groups, own style) still applies.
  if (!Array.isArray(layout.groups)) layout = { ...clone(defaults), ...(layout.style ? { style: layout.style } : {}), ...(Array.isArray(layout.pinned) ? { pinned: layout.pinned } : {}), ...(Array.isArray(layout.popouts) ? { popouts: layout.popouts } : {}) };
  const popouts = [...new Set((Array.isArray(layout.popouts) ? layout.popouts : []).filter(k => hubOf(k) && !memberTab(k).noTab))];
  const known = new Set([...defaults.groups.flatMap(g => g.items), ...popouts]);
  const seen = new Set();
  const take = k => known.has(k) && !seen.has(k) && seen.add(k);
  const out = {
    groups: layout.groups.map(g => ({
      name: String(g?.name || '').slice(0, 40),
      items: unsplit(Array.isArray(g?.items) ? g.items : []).filter(take),
    })),
    hidden: unsplit(Array.isArray(layout.hidden) ? layout.hidden : []).filter(take),
    style: STYLE_IDS.includes(layout.style) ? layout.style : 'list',
    pinned: Array.isArray(layout.pinned) ? [...new Set(unsplit(layout.pinned).filter(k => known.has(k)))] : clone(defaults.pinned),
    popouts,
  };
  if (!out.groups.length) out.groups.push({ name: 'Menu', items: [] });
  defaults.groups.forEach(dg => dg.items.forEach(k => {
    if (seen.has(k)) return;
    seen.add(k);
    const home = out.groups.find(g => g.name.toLowerCase() === dg.name.toLowerCase()) || out.groups[out.groups.length - 1];
    home.items.push(k);
  }));
  popouts.forEach(k => { if (!seen.has(k)) { seen.add(k); placeAfterHub(out, k); } });
  return out;
}

const clone = o => JSON.parse(JSON.stringify(o));

function render(layout) {
  const root = bar();
  if (!root) return;
  current = layout;
  // Hubs follow their pages (switched off, removed for this role) before the groups are laid out.
  setPopouts(layout.popouts);
  syncHubs();
  root.querySelectorAll(':scope > .rail-group-header').forEach(h => h.remove());
  let holder = document.getElementById('rail-hidden-items');
  if (!holder) {
    holder = document.createElement('div');
    holder.id = 'rail-hidden-items';
    holder.hidden = true;
  }
  // A hub's pages without their own link go back off stage (they are its tabs).
  const members = document.getElementById('rail-hub-members');
  if (members) MEMBER_KEYS.forEach(k => {
    const el = document.getElementById(k);
    if (el && !layout.popouts.includes(k) && el.parentElement !== members) members.appendChild(el);
  });
  layout.groups.forEach(g => {
    // Items removed for this user's role (app.js drops them for dock staff) are skipped,
    // and so is a header with nothing left under it.
    const els = g.items.map(k => document.getElementById(k)).filter(Boolean);
    if (!els.length) return;
    if (g.name && els.some(el => !el.classList.contains('feature-off'))) {
      const h = document.createElement('div');
      h.className = 'rail-group-header';
      h.textContent = g.name;
      root.appendChild(h);
    }
    els.forEach(el => root.appendChild(el));
  });
  layout.hidden.forEach(k => { const el = document.getElementById(k); if (el) holder.appendChild(el); });
  root.appendChild(holder);
  if (members) root.appendChild(members);
  applyRailStyle(layout, { onPin: pinFromLauncher });
}

/** ☆ in All pages: pin or unpin a link and save. A hub's page pins as its own rail link. */
function pinFromLauncher(key, on) {
  if (!current) return;
  const next = clone(current);
  next.pinned = (next.pinned || []).filter(k => k !== key);
  if (hubOf(key)) {
    if (!on) dropPopout(next, key);
    else if (!next.popouts.includes(key)) { next.popouts.push(key); placeAfterHub(next, key); }
  }
  if (on) next.pinned.push(key);
  save(normalize(next)).catch(err => console.error('[railLayout] pin failed', err));
}

function cacheRead() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || 'null'); } catch { return null; }
}
function cacheWrite(layout) {
  try {
    if (layout) localStorage.setItem(CACHE_KEY, JSON.stringify(layout));
    else localStorage.removeItem(CACHE_KEY);
  } catch { /* storage blocked — the database copy still applies */ }
}

async function save(layout) {
  const isDefault = JSON.stringify(layout) === JSON.stringify(defaults);
  cacheWrite(isDefault ? null : layout);
  render(layout);
  if (!uid) return;
  await set(ref(database, `userPrefs/${uid}/railLayout`), isDefault ? null : { ...layout, updatedAt: Date.now() });
}

/* ============ EDITOR ============ */

let draft = null;
let dragKey = null;
let openHubs = new Set(); // hubs whose pages are listed in the editor

/** A hub row's pages, each with an "Own link on rail" switch. */
function hubPanel(key) {
  const tabs = hubTabs(key);
  return `<li class="rl-hub-panel" data-hub="${escapeHTML(key)}">
    <div class="rl-hub-head"><span>Tab</span><span>Own link on rail</span></div>
    ${tabs.map(t => {
      const on = draft.popouts.includes(t.key);
      return `<div class="rl-hub-tab"><span>${escapeHTML(t.label)}</span>
        <button type="button" class="rl-switch" role="switch" aria-checked="${on}" data-act="popout" data-member="${escapeHTML(t.key)}"
          aria-label="Give ${escapeHTML(itemLabel(t.key))} its own link on the rail"><i></i></button></div>`;
    }).join('')}
  </li>`;
}

function editorRow(key, gi) {
  const icon = itemIcon(key);
  const hub = isHub(key);
  const from = hubOf(key);
  const open = hub && openHubs.has(key);
  const n = hub ? hubTabs(key).length : 0;
  return `<li class="rl-item${open ? ' rl-item-open' : ''}" draggable="true" data-key="${escapeHTML(key)}" data-g="${gi}">
    <span class="rl-grip" aria-hidden="true">⋮⋮</span>
    ${icon ? `<img src="${escapeHTML(icon)}" alt="" width="15" height="15">` : '<span class="rl-noicon" aria-hidden="true"></span>'}
    <span class="rl-label-wrap"><span class="rl-name">${escapeHTML(itemLabel(key))}</span>${hub && n > 1 ? `<button type="button" class="rl-hub-toggle" data-act="hub-toggle" aria-expanded="${open}">${n} pages ${open ? '▾' : '▸'}</button>` : ''}${from ? `<span class="rl-from">from ${escapeHTML(HUBS[from].label)}</span>` : ''}</span>
    ${draft.style === 'pinned' && gi !== 'hidden' ? `<button type="button" class="rl-btn rl-pin${draft.pinned.includes(key) ? ' on' : ''}" data-act="item-pin" aria-pressed="${draft.pinned.includes(key)}" title="${draft.pinned.includes(key) ? 'Unpin' : 'Pin to your rail'}" aria-label="${draft.pinned.includes(key) ? 'Unpin' : 'Pin'} ${escapeHTML(itemLabel(key))}">${draft.pinned.includes(key) ? '★' : '☆'}</button>` : ''}
    <button type="button" class="rl-btn" data-act="item-up" title="Move up" aria-label="Move ${escapeHTML(itemLabel(key))} up">↑</button>
    <button type="button" class="rl-btn" data-act="item-down" title="Move down" aria-label="Move ${escapeHTML(itemLabel(key))} down">↓</button>
    <select class="rl-move" data-act="item-move" aria-label="Group for ${escapeHTML(itemLabel(key))}">
      ${draft.groups.map((g, i) => `<option value="${i}"${i === gi ? ' selected' : ''}>${escapeHTML(g.name || 'Untitled group')}</option>`).join('')}
      <option value="hidden"${gi === 'hidden' ? ' selected' : ''}>Hidden</option>
    </select>
  </li>${open ? hubPanel(key) : ''}`;
}

function paintEditor() {
  const styles = document.getElementById('rl-styles');
  styles.innerHTML = RAIL_STYLES.map(st => `<label class="rl-style${draft.style === st.id ? ' on' : ''}">
      <input type="radio" name="rl-style" value="${st.id}"${draft.style === st.id ? ' checked' : ''}>
      <span class="rl-style-pic rl-style-pic-${st.id}" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
      <span class="rl-style-text"><b>${escapeHTML(st.name)}</b><span>${escapeHTML(st.note)}</span></span></label>`).join('');
  document.getElementById('rl-pin-note').hidden = draft.style !== 'pinned';
  const body = document.getElementById('rl-groups');
  body.innerHTML = draft.groups.map((g, gi) => `
    <section class="rl-group" data-g="${gi}">
      <div class="rl-group-head">
        <input class="rl-group-name" data-g="${gi}" value="${escapeHTML(g.name)}" maxlength="40" placeholder="Group name" aria-label="Group name">
        <button type="button" class="rl-btn" data-act="group-up" title="Move group up" aria-label="Move group up"${gi === 0 ? ' disabled' : ''}>↑</button>
        <button type="button" class="rl-btn" data-act="group-down" title="Move group down" aria-label="Move group down"${gi === draft.groups.length - 1 ? ' disabled' : ''}>↓</button>
        <button type="button" class="rl-btn rl-btn-danger" data-act="group-delete" title="Remove group (its links move to the group above)" aria-label="Remove group"${draft.groups.length === 1 ? ' disabled' : ''}>✕</button>
      </div>
      <ul class="rl-list" data-g="${gi}">${g.items.filter(k => !switchedOff(k)).map(k => editorRow(k, gi)).join('') || '<li class="rl-empty">Drag links here</li>'}</ul>
    </section>`).join('') + `
    <section class="rl-group rl-group-hidden" data-g="hidden">
      <div class="rl-group-head"><span class="rl-hidden-title">Hidden</span><span class="rl-hidden-sub">Not shown in your menu</span></div>
      <ul class="rl-list" data-g="hidden">${draft.hidden.filter(k => !switchedOff(k)).map(k => editorRow(k, 'hidden')).join('') || '<li class="rl-empty">Drag links here to hide them</li>'}</ul>
    </section>`;
}

const listOf = g => (g === 'hidden' ? draft.hidden : draft.groups[g].items);
const parseG = v => (v === 'hidden' ? 'hidden' : Number(v));

function moveItem(key, toG, toIndex) {
  draft.groups.forEach(g => { g.items = g.items.filter(k => k !== key); });
  draft.hidden = draft.hidden.filter(k => k !== key);
  const list = listOf(toG);
  list.splice(toIndex == null ? list.length : Math.min(toIndex, list.length), 0, key);
}

function onEditorClick(e) {
  const btn = e.target.closest('[data-act]');
  if (!btn || btn.tagName === 'SELECT') return;
  const act = btn.dataset.act;
  if (act === 'add-group') {
    draft.groups.push({ name: 'New group', items: [] });
    paintEditor();
    const inputs = document.querySelectorAll('#rl-groups .rl-group-name');
    inputs[inputs.length - 1]?.select();
    return;
  }
  if (act === 'reset') { draft = clone(defaults); paintEditor(); return; }
  if (act === 'cancel') { closeEditor(); return; }
  if (act === 'save') { saveEditor(); return; }
  if (act === 'hub-toggle') {
    const key = btn.closest('.rl-item').dataset.key;
    if (openHubs.has(key)) openHubs.delete(key); else openHubs.add(key);
    paintEditor();
    document.querySelector(`#rl-groups .rl-item[data-key="${CSS.escape(key)}"] .rl-hub-toggle`)?.focus();
    return;
  }
  if (act === 'popout') {
    const key = btn.dataset.member;
    if (draft.popouts.includes(key)) dropPopout(draft, key);
    else { draft.popouts.push(key); placeAfterHub(draft, key); }
    paintEditor();
    document.querySelector(`#rl-groups .rl-switch[data-member="${CSS.escape(key)}"]`)?.focus();
    return;
  }
  const gEl = btn.closest('.rl-group');
  const gi = parseG(gEl?.dataset.g);
  if (act.startsWith('group-')) {
    const gs = draft.groups;
    if (act === 'group-up' && gi > 0) [gs[gi - 1], gs[gi]] = [gs[gi], gs[gi - 1]];
    if (act === 'group-down' && gi < gs.length - 1) [gs[gi + 1], gs[gi]] = [gs[gi], gs[gi + 1]];
    if (act === 'group-delete' && gs.length > 1) {
      const [gone] = gs.splice(gi, 1);
      gs[Math.max(0, gi - 1)].items.push(...gone.items);
    }
    paintEditor();
    return;
  }
  const key = btn.closest('.rl-item')?.dataset.key;
  if (!key) return;
  if (act === 'item-pin') {
    draft.pinned = draft.pinned.includes(key) ? draft.pinned.filter(k => k !== key) : [...draft.pinned, key];
    paintEditor();
    document.querySelector(`#rl-groups .rl-item[data-key="${CSS.escape(key)}"] [data-act="item-pin"]`)?.focus();
    return;
  }
  const list = listOf(gi);
  const i = list.indexOf(key);
  // Swap with the nearest neighbour the user can see (switched-off links keep their place).
  let j = i;
  do { j += act === 'item-up' ? -1 : 1; } while (j >= 0 && j < list.length && switchedOff(list[j]));
  if ((act === 'item-up' || act === 'item-down') && j >= 0 && j < list.length) [list[j], list[i]] = [list[i], list[j]];
  paintEditor();
  document.querySelector(`#rl-groups .rl-item[data-key="${CSS.escape(key)}"] [data-act="${act}"]`)?.focus();
}

function onEditorChange(e) {
  if (e.target.name === 'rl-style') { draft.style = e.target.value; paintEditor(); return; }
  if (e.target.matches('.rl-move')) {
    moveItem(e.target.closest('.rl-item').dataset.key, parseG(e.target.value));
    paintEditor();
  }
}

function onEditorInput(e) {
  if (e.target.matches('.rl-group-name')) {
    draft.groups[Number(e.target.dataset.g)].name = e.target.value;
    // Keep the "move to group" pickers' names in step without repainting (keeps focus).
    document.querySelectorAll(`#rl-groups .rl-move option[value="${e.target.dataset.g}"]`)
      .forEach(o => { o.textContent = e.target.value || 'Untitled group'; });
  }
}

function wireDrag(root) {
  root.addEventListener('dragstart', (e) => {
    const li = e.target.closest?.('.rl-item');
    if (!li) return;
    dragKey = li.dataset.key;
    li.classList.add('rl-dragging');
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', dragKey);
  });
  root.addEventListener('dragend', () => {
    dragKey = null;
    root.querySelectorAll('.rl-dragging, .rl-drop-target').forEach(el => el.classList.remove('rl-dragging', 'rl-drop-target'));
  });
  root.addEventListener('dragover', (e) => {
    const list = e.target.closest?.('.rl-list');
    if (!dragKey || !list) return;
    e.preventDefault();
    root.querySelectorAll('.rl-drop-target').forEach(el => el.classList.remove('rl-drop-target'));
    list.classList.add('rl-drop-target');
  });
  root.addEventListener('drop', (e) => {
    const list = e.target.closest?.('.rl-list');
    if (!dragKey || !list) return;
    e.preventDefault();
    // Drop before the row under the pointer (or after it if the pointer is on its lower half).
    const rows = [...list.querySelectorAll('.rl-item')].filter(r => r.dataset.key !== dragKey);
    let index = rows.length;
    for (let i = 0; i < rows.length; i++) {
      const b = rows[i].getBoundingClientRect();
      if (e.clientY < b.top + b.height / 2) { index = i; break; }
    }
    moveItem(dragKey, parseG(list.dataset.g), index);
    dragKey = null;
    paintEditor();
  });
}

function buildEditor() {
  const wrap = document.createElement('div');
  wrap.id = 'rail-layout-editor';
  wrap.className = 'rl-scrim';
  wrap.hidden = true;
  wrap.innerHTML = `
    <div class="rl-modal" role="dialog" aria-modal="true" aria-labelledby="rl-title">
      <header class="rl-header">
        <div>
          <h2 id="rl-title">Customize your menu</h2>
          <p>Pick how the menu looks; rename, add and reorder groups; drag links between them, or hide ones you don't use. Open a hub to give one of its pages its own link. Saved to your profile, so it follows you to any device.</p>
        </div>
        <button type="button" class="rl-close" data-act="cancel" aria-label="Close">✕</button>
      </header>
      <div class="rl-body">
        <div class="rl-styles" id="rl-styles" role="radiogroup" aria-label="Menu style"></div>
        <p class="rl-pin-note" id="rl-pin-note" hidden>★ pins a link to your rail (in the order you pin them). The rest are in <b>All pages</b>.</p>
        <div id="rl-groups"></div>
      </div>
      <footer class="rl-footer">
        <button type="button" class="rl-text-btn" data-act="add-group">+ Add group</button>
        <button type="button" class="rl-text-btn" data-act="reset">Reset to default</button>
        <span class="rl-spacer"></span>
        <button type="button" class="rl-cancel" data-act="cancel">Cancel</button>
        <button type="button" class="rl-save" data-act="save">Save</button>
      </footer>
    </div>`;
  document.body.appendChild(wrap);
  wrap.addEventListener('click', (e) => { if (e.target === wrap) closeEditor(); else onEditorClick(e); });
  wrap.addEventListener('change', onEditorChange);
  wrap.addEventListener('input', onEditorInput);
  wrap.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); closeEditor(); } });
  wireDrag(wrap);
  return wrap;
}

export function openRailLayoutEditor() {
  const wrap = document.getElementById('rail-layout-editor') || buildEditor();
  draft = clone(current || defaults);
  openHubs = new Set();
  paintEditor();
  wrap.hidden = false;
  wrap.querySelector('.rl-save').focus();
}

function closeEditor() {
  const wrap = document.getElementById('rail-layout-editor');
  if (wrap) wrap.hidden = true;
  draft = null;
  document.getElementById('user-data')?.focus(); // Customize menu lives in the user menu
}

async function saveEditor() {
  const layout = normalize({
    groups: draft.groups.map(g => ({ name: g.name.trim(), items: g.items })),
    hidden: draft.hidden,
    style: draft.style,
    pinned: draft.pinned.filter(k => !draft.hidden.includes(k)),
    popouts: draft.popouts,
  });
  closeEditor();
  try {
    await save(layout);
  } catch (err) {
    console.error('[railLayout] save failed', err);
    window.alert("Your menu changed on this device, but it couldn't be saved to your profile. Try again when you're back online.");
  }
}

/** Re-lay the rail after features are switched on or off. */
export function refreshRail() {
  if (current) render(current);
}

/* ============ INIT ============ */

export function initRailLayout() {
  if (!bar() || defaults) return;
  defaults = readDefaults();
  const cached = cacheRead();
  if (cached) render(normalize(cached));
  else render(clone(defaults));
  document.getElementById('rail-customize-button')?.addEventListener('click', (e) => {
    e.stopPropagation(); // .page-buttons clicks close the mobile menu
    openRailLayoutEditor();
  });
  onAuthStateChanged(auth, async (user) => {
    uid = user?.uid || null;
    if (!uid) return;
    try {
      const snap = await get(ref(database, `userPrefs/${uid}/railLayout`));
      const saved = snap.val();
      const layout = normalize(saved);
      cacheWrite(saved ? layout : null);
      render(layout);
    } catch { /* offline or no access yet — the cached layout stays */ }
  });
}
